import React from "react";

const PrivateRoutes = React.lazy(() => import("../auth/Auth"));
const RoleBasedHomeRoute = React.lazy(() => import("./public/RoleBasedHomeRoute"));
const RoleBasedCatchAllRoute = React.lazy(() => import("./public/RoleBasedCatchAllRoute"));

const ResetPassword = React.lazy(() => import("../admin-module/sign-in/reset-password"));
const OtpVerification = React.lazy(() => import("../admin-module/sign-in/otp-verification"));
const ChangePassword = React.lazy(() => import("../admin-module/sign-in/new-password"));

const Users = React.lazy(() => import("../admin-module/Users"));
const IndividualProfileDetail = React.lazy(() =>
  import("../admin-module/Users/individual-profile-detail/individual-profile-detail")
);
const GroupDetail = React.lazy(() =>
  import("../admin-module/Groups/group-detail/group-detail")
);
const ExistingWorkout = React.lazy(() =>
  import("../admin-module/Groups/existing-workout/existing-workout")
);
const NewWorkout = React.lazy(() => import("../admin-module/Groups/new-workout/new-workout"));
const CreateGroup = React.lazy(() =>
  import("../admin-module/Groups/create-new-group/create-new-group")
);
const TaskHome = React.lazy(() => import("../admin-module/Tasks/tasks-home/task-home"));

const CreateProfile = React.lazy(() =>
  import("../admin-module/Users/create-profile/create-profile")
);
const UserLayout = React.lazy(() =>
  import("../admin-module/layout/user-module-layout/user-module-layout")
);
const UserDetail = React.lazy(() =>
  import("../admin-module/Users/user-detail/user-detail")
);
const CreateUser = React.lazy(() => import("../admin-module/Users/create-user/create-user"));
const EditUser = React.lazy(() => import("../admin-module/Users/edit-user/edit-user"));
const Settings = React.lazy(() => import("../admin-module/Settings"));

const ChallengeLayout = React.lazy(() => import("../admin-module/layout/challenge-layout"));
const Challenges = React.lazy(() => import("../admin-module/Challenges"));
const ChallengeDetail = React.lazy(() =>
  import("../admin-module/Challenges/challenge-detail/challenge-detail")
);
const CreateFeatureWorkout = React.lazy(() =>
  import("../admin-module/Challenges/create-new-fw/create-new-fw")
);
const CreateNewWorkout = React.lazy(() =>
  import("../admin-module/Challenges/new-workout/new-workout")
);
const EditWorkout = React.lazy(() =>
  import("../admin-module/Challenges/edit-workout/edit-workout")
);
const WorkoutDetail = React.lazy(() =>
  import("../admin-module/Challenges/workout-detail/workout-detail")
);
const EditFW = React.lazy(() => import("../admin-module/Challenges/edit-fw/edit-fw"));

const SubmissionLayout = React.lazy(() => import("../admin-module/layout/submission-layout"));
const SubmissionsMain = React.lazy(() => import("../admin-module/Submissions"));
const SubmissionDetail = React.lazy(() =>
  import("../admin-module/Submissions/submission-detail/submission-detail")
);
const FWSubmissionDetail = React.lazy(() =>
  import("../admin-module/Submissions/FW-submission-detail/FW-submission-detail")
);

const AllVideosLayout = React.lazy(() => import("../admin-module/layout/all-videos-layout"));
const AllVideos = React.lazy(() => import("../admin-module/all-videos"));
const BroadcastMessage = React.lazy(() => import("../admin-module/broadcast-message"));
const TaxExemptions = React.lazy(() => import("../admin-module/TaxExemptions"));

const TeamsLayout = React.lazy(() => import("../admin-module/layout/teams-layout"));
const Teams = React.lazy(() => import("../admin-module/Teams"));
const ManageSubscriptionCoach = React.lazy(() =>
  import("../admin-module/Teams/manage-subscription")
);
const CreateTeamGroup = React.lazy(() =>
  import("../admin-module/Teams/create-team-group")
);

const FeatureAssignedWorkout = React.lazy(() =>
  import("../admin-module/Groups/assigned-feature-workout/assigned-feature-workout")
);

const RssFeedLayout = React.lazy(() => import("../admin-module/layout/rss-feed-layout"));
const RssFeed = React.lazy(() => import("../admin-module/RSS-Feed"));
const SpotlightPage = React.lazy(() => import("../admin-module/RSS-Feed/spotlight"));

/**
 * Central route config array.
 * Route modules load on demand via React.lazy (code splitting).
 */
export const appRoutesConfig = [
  { path: "/", element: <RoleBasedHomeRoute /> },
  { path: "reset-password", element: <ResetPassword /> },
  { path: "otp-verification", element: <OtpVerification /> },
  { path: "change-password", element: <ChangePassword /> },

  {
    path: "home",
    element: <PrivateRoutes />,
    children: [
      {
        path: "rss-feed",
        element: <RssFeedLayout />,
        children: [
          { index: true, element: <RssFeed /> },
          { path: "spotlight", element: <SpotlightPage /> },
        ],
      },

      {
        path: "users",
        element: <UserLayout />,
        children: [
          { index: true, element: <Users /> },
          { path: "user-accounts", element: <UserDetail /> },
          { path: "profiles", element: <IndividualProfileDetail /> },
          { path: "create-profile", element: <CreateProfile /> },
          { path: "create-user", element: <CreateUser /> },
          { path: "edit-user", element: <EditUser /> },
        ],
      },

      {
        path: "teams",
        element: <TeamsLayout />,
        children: [
          { index: true, element: <Teams /> },
          { path: "create-team-group", element: <CreateTeamGroup /> },
          { path: "groups", element: <ManageSubscriptionCoach /> },
          { path: "groups/detail", element: <GroupDetail /> },
          { path: "groups/create-new-group", element: <CreateGroup /> },
          { path: "groups/assigned-workout", element: <FeatureAssignedWorkout /> },
          {
            path: "manage-subscription",
            element: <ManageSubscriptionCoach />,
          },
          {
            path: "assign-multiple-existing-workouts",
            element: <ExistingWorkout />,
          },
          {
            path: "assign-multiple-new-workouts",
            element: <NewWorkout />,
          },
        ],
      },

      {
        path: "workouts",
        element: <ChallengeLayout />,
        children: [
          { index: true, element: <Challenges /> },
          { path: ":id", element: <ChallengeDetail /> },
          {
            path: "create-new-featured-workout",
            element: <CreateFeatureWorkout />,
          },
          { path: "edit-featured-workout", element: <EditFW /> },
          { path: "create-new-workout", element: <CreateNewWorkout /> },
          { path: "edit-workout", element: <EditWorkout /> },
          { path: "workout-detail", element: <WorkoutDetail /> },
        ],
      },

      {
        path: "submissions",
        element: <SubmissionLayout />,
        children: [
          { index: true, element: <SubmissionsMain /> },
          {
            path: "bsa-detail/:id",
            element: <SubmissionDetail />,
          },
          { path: ":id?/:slug?", element: <FWSubmissionDetail /> },
        ],
      },

      {
        path: "all-videos",
        element: <AllVideosLayout />,
        children: [{ index: true, element: <AllVideos /> }],
      },

      { path: "broadcast-message", element: <BroadcastMessage /> },
      { path: "tax-exemptions", element: <TaxExemptions /> },

      { path: "tasks", element: <TaskHome /> },
      { path: "new-workout", element: <NewWorkout /> },
      { path: "existing-workout", element: <ExistingWorkout /> },

      {
        path: "settings",
        element: <Settings />,
      },
    ],
  },

  { path: "*", element: <RoleBasedCatchAllRoute /> },
];
